import { recordApiResponse } from '#/auth/authChangeWatcher'
import { getCsrfToken } from '#/utils'
import { ServerError } from './ServerError'
import {
  bridgeOrvalFailureToLegacyActions,
  bridgeOrvalStartToLegacyActions,
  bridgeOrvalSuccessToLegacyActions,
} from './reflux-bridge'

interface FetchWithAuthConfig extends RequestInit {
  method?: 'GET' | 'PUT' | 'POST' | 'PATCH' | 'DELETE'
}

/**
 * Central HTTP mutator used by all Orval-generated clients.
 *
 * Why this file exists:
 * - It standardizes auth headers and error handling for generated API calls.
 * - It is the single best interception point for successful and failed mutations,
 *   regardless of whether callers use `useMutation` hooks or direct async calls.
 *
 * Migration goal:
 * - During Reflux → react-query migration, we emit selected legacy Reflux
 *   actions after Orval mutation responses so old listeners keep updating while
 *   components are incrementally migrated.
 * - Once legacy listeners are retired, bridge emission can be removed from
 *   here in one place.
 *
 * On error throws either TypeError, NotAllowedError, AbortError (see MDN) or {@link ServerError}
 */
export const fetchWithAuth = async <T>(url: string, config: FetchWithAuthConfig): Promise<T> => {
  const csrfToken = getCsrfToken()
  // For multipart requests, browser must set Content-Type (including boundary) automatically.
  // If we force application/json here, uploads like /api/v2/imports/ break.
  const hasFormDataBody = typeof FormData !== 'undefined' && config.body instanceof FormData

  // Request-time legacy lifecycle callbacks such as `started` must fire before
  // the fetch so they remain distinct from response-time `completed` callbacks.
  bridgeOrvalStartToLegacyActions(url, config)

  const response = await fetch(url, {
    ...config,
    headers: {
      ...config.headers,
      Accept: 'application/json',
      // Pass authentication data only when it's required.
      ...(config.method !== 'GET' && !hasFormDataBody ? { 'Content-Type': 'application/json' } : null),
      ...(config.method !== 'GET' && csrfToken ? { 'X-CSRFToken': csrfToken } : null),
    },
  })

  if (!response.ok) {
    // A 401 here is how an expired session reaches the auth guards - see `#/auth/authChangeWatcher`
    let error: ServerError
    try {
      error = await ServerError.new(response)
    } catch (bodyReadFailure) {
      // The body can fail on its own, with the connection dropping after the headers arrived. Report the status
      // anyway: without it a page keeps answering "signed in" from cache for the rest of `staleTime`.
      recordApiResponse({ url, status: response.status })
      throw bodyReadFailure
    }

    recordApiResponse({ url, status: response.status, body: error.parsedResponse })

    bridgeOrvalFailureToLegacyActions(url, config, {
      error,
      data: error.parsedResponse,
      status: response.status,
      headers: response.headers,
    })

    throw error
  }

  const result = {
    data:
      response.status !== 204 && response.headers.get('content-type')?.indexOf('application/json') !== -1
        ? await response.json()
        : {},
    status: response.status,
    headers: response.headers,
  } as T

  // Bridge successful mutation responses to legacy Reflux `.completed` events.
  // Failed responses are bridged before throwing so legacy `.failed` handlers
  // can continue to show the same alerts and recovery paths.
  bridgeOrvalSuccessToLegacyActions(url, config, result as { data: unknown; status: number; headers: Headers })

  return result
}

export default fetchWithAuth
